import { atom } from "jotai";
import { GraphQlAdapter } from "@/infrastructure/adapters/graphQl";
import { GetPurchaserUseCase } from "@/core/usecases/lookup/GetPurchaser.useCase";
import { lookupAtom } from "./lookup.atoms";
import {
  GetGLCodeUseCase,
  GetDocumentTypeUseCase,
  GetDeductionTypeUseCase,
  GetDistributionCenterUseCase,
  GetPriorityUseCase,
  GetStageUseCase,
  GetValidityUseCase,
  GetBrandMemberUseCase,
  GetAdjustmentTypeUseCase,
  GetAdjustmentCategoryUseCase,
  GetDeductionTypeCategoryUseCase,
  GetAccountingSystemUseCase,
  GetRetailerUseCase,
} from "@/core/usecases/lookup";
import { GetPromoTypeUseCase } from "@/core/usecases/lookup/GetPromoType.useCase";
import { GetPromoStatusUseCase } from "@/core/usecases/lookup/GetPromoStatus.useCase";
import { GetDisputeTrackingStatusUseCase } from "@/core/usecases/lookup/GetDisputeTrackingStatus.useCase";
import { GetProductLookUpUseCase } from "@/core/usecases/lookup/GetProductLookUp.useCase";
import { LookupRepository } from "@/infrastructure/repositories/lookup";
import { GetDocumentSubtypeUseCase } from "@/core/usecases/lookup/GetDocumentSubtype.useCase";

// GraphQL client
const client = GraphQlAdapter.getInstance().getClient();

// Repositories
const lookupRepository = new LookupRepository(client);

/**
 * Purchasers
 */
export const getPurchasersAtom = atom(null, async (get, set) => {
  set(lookupAtom, {
    ...get(lookupAtom),
    purchasers: {
      data: get(lookupAtom).purchasers.data,
      count: get(lookupAtom).purchasers.count,
      isLoading: true,
      error: null,
    },
  });
  try {
    const purchasers = await new GetPurchaserUseCase(lookupRepository).execute();
    set(lookupAtom, {
      ...get(lookupAtom),
      purchasers: {
        data: purchasers.purchasers,
        count: purchasers.count,
        isLoading: false,
        error: null,
      },
    });
  } catch (error: unknown) {
    set(lookupAtom, {
      ...get(lookupAtom),
      purchasers: {
        data: get(lookupAtom).purchasers.data,
        count: get(lookupAtom).purchasers.count,
        isLoading: false,
        error: error as string,
      },
    });
  }
});

/**
 * Retailers
 */
export const getRetailersAtom = atom(null, async (get, set) => {
  set(lookupAtom, {
    ...get(lookupAtom),
    retailers: {
      data: get(lookupAtom).retailers.data,
      count: get(lookupAtom).retailers.count,
      isLoading: true,
      error: null,
    },
  });
  try {
    const retailers = await new GetRetailerUseCase(lookupRepository).execute();
    set(lookupAtom, {
      ...get(lookupAtom),
      retailers: {
        data: retailers.retailers,
        count: retailers.count,
        isLoading: false,
        error: null,
      },
    });
  } catch (error: unknown) {
    set(lookupAtom, {
      ...get(lookupAtom),
      retailers: {
        data: get(lookupAtom).retailers.data,
        count: get(lookupAtom).retailers.count,
        isLoading: false,
        error: error as string,
      },
    });
  }
});

/**
 * Distribution Centers
 */
export const getDistributionCentersAtom = atom(null, async (get, set, params?: { purchaserId?: number }) => {
  set(lookupAtom, {
    ...get(lookupAtom),
    distributionCenters: {
      data: get(lookupAtom).distributionCenters.data,
      count: get(lookupAtom).distributionCenters.count,
      isLoading: true,
      error: null,
    },
  });
  try {
    const distributionCenters = await new GetDistributionCenterUseCase(lookupRepository).execute(params);
    set(lookupAtom, {
      ...get(lookupAtom),
      distributionCenters: {
        data: distributionCenters.distributionCenters,
        count: distributionCenters.count,
        isLoading: false,
        error: null,
      },
    });
  } catch (error: unknown) {
    set(lookupAtom, {
      ...get(lookupAtom),
      distributionCenters: {
        data: get(lookupAtom).distributionCenters.data,
        count: get(lookupAtom).distributionCenters.count,
        isLoading: false,
        error: error as string,
      },
    });
  }
});

/**
 * Priorities
 */
export const getPrioritiesAtom = atom(null, async (get, set) => {
  set(lookupAtom, {
    ...get(lookupAtom),
    priorities: {
      data: get(lookupAtom).priorities.data,
      count: get(lookupAtom).priorities.count,
      isLoading: true,
      error: null,
    },
  });
  try {
    const priorities = await new GetPriorityUseCase(lookupRepository).execute();
    set(lookupAtom, {
      ...get(lookupAtom),
      priorities: {
        data: priorities.priorities,
        count: priorities.count,
        isLoading: false,
        error: null,
      },
    });
  } catch (error: unknown) {
    set(lookupAtom, {
      ...get(lookupAtom),
      priorities: {
        data: get(lookupAtom).priorities.data,
        count: get(lookupAtom).priorities.count,
        isLoading: false,
        error: error as string,
      },
    });
  }
});

/**
 * Validities
 */
export const getValiditiesAtom = atom(null, async (get, set) => {
  set(lookupAtom, {
    ...get(lookupAtom),
    validities: {
      data: get(lookupAtom).validities.data,
      count: get(lookupAtom).validities.count,
      isLoading: true,
      error: null,
    },
  });
  try {
    const validities = await new GetValidityUseCase(lookupRepository).execute();
    set(lookupAtom, {
      ...get(lookupAtom),
      validities: {
        data: validities.validities,
        count: validities.count,
        isLoading: false,
        error: null,
      },
    });
  } catch (error: unknown) {
    set(lookupAtom, {
      ...get(lookupAtom),
      validities: {
        data: get(lookupAtom).validities.data,
        count: get(lookupAtom).validities.count,
        isLoading: false,
        error: error as string,
      },
    });
  }
});

/**
 * Stages
 */
export const getStagesAtom = atom(null, async (get, set) => {
  set(lookupAtom, {
    ...get(lookupAtom),
    stages: {
      data: get(lookupAtom).stages.data,
      count: get(lookupAtom).stages.count,
      isLoading: true,
      error: null,
    },
  });
  try {
    const stages = await new GetStageUseCase(lookupRepository).execute();

    set(lookupAtom, {
      ...get(lookupAtom),
      stages: {
        data: stages.stages,
        count: stages.count,
        isLoading: false,
        error: null,
      },
    });
  } catch (error: unknown) {
    set(lookupAtom, {
      ...get(lookupAtom),
      stages: {
        data: get(lookupAtom).stages.data,
        count: get(lookupAtom).stages.count,
        isLoading: false,
        error: error as string,
      },
    });
  }
});

/**
 * Deduction Types
 */
export const getDeductionTypesAtom = atom(null, async (get, set) => {
  set(lookupAtom, {
    ...get(lookupAtom),
    deductionTypes: {
      data: get(lookupAtom).deductionTypes.data,
      count: get(lookupAtom).deductionTypes.count,
      isLoading: true,
      error: null,
    },
  });
  try {
    const deductionTypes = await new GetDeductionTypeUseCase(lookupRepository).execute();
    set(lookupAtom, {
      ...get(lookupAtom),
      deductionTypes: {
        data: deductionTypes.deductionTypes,
        count: deductionTypes.count,
        isLoading: false,
        error: null,
      },
    });
  } catch (error: unknown) {
    set(lookupAtom, {
      ...get(lookupAtom),
      deductionTypes: {
        data: get(lookupAtom).deductionTypes.data,
        count: get(lookupAtom).deductionTypes.count,
        isLoading: false,
        error: error as string,
      },
    });
  }
});

/**
 * GL Codes
 */
export const getGLCodesAtom = atom(null, async (get, set) => {
  set(lookupAtom, {
    ...get(lookupAtom),
    glCodes: {
      data: get(lookupAtom).glCodes.data,
      count: get(lookupAtom).glCodes.count,
      isLoading: true,
      error: null,
    },
  });
  try {
    const glCodes = await new GetGLCodeUseCase(lookupRepository).execute();
    set(lookupAtom, {
      ...get(lookupAtom),
      glCodes: {
        data: glCodes.glCodes,
        count: glCodes.count,
        isLoading: false,
        error: null,
      },
    });
  } catch (error: unknown) {
    set(lookupAtom, {
      ...get(lookupAtom),
      glCodes: {
        data: get(lookupAtom).glCodes.data,
        count: get(lookupAtom).glCodes.count,
        isLoading: false,
        error: error as string,
      },
    });
  }
});

/**
 * Brand Members
 */
export const getBrandMembersAtom = atom({
  include_deleted: false,
  include_bots: false,
  role_key: []
}, async (get, set, { include_deleted, include_bots, role_key }) => {
  set(lookupAtom, {
    ...get(lookupAtom),
    brandMembers: {
      data: get(lookupAtom).brandMembers.data,
      count: get(lookupAtom).brandMembers.count,
      isLoading: true,
      error: null,
    },
  });
  try {
    const brandMembers = await new GetBrandMemberUseCase(lookupRepository).execute({
      include_deleted: include_deleted,
      include_bots: include_bots,
      role_key: role_key
    });
    set(lookupAtom, {
      ...get(lookupAtom),
      brandMembers: {
        data: brandMembers.brandMembers,
        count: brandMembers.count,
        isLoading: false,
        error: null,
      },
    });
  } catch (error: unknown) {
    set(lookupAtom, {
      ...get(lookupAtom),
      brandMembers: {
        data: get(lookupAtom).brandMembers.data,
        count: get(lookupAtom).brandMembers.count,
        isLoading: false,
        error: error as string,
      },
    });
  }
});

/**
 * Document Types
 */
export const getDocumentTypesAtom = atom(null, async (get, set) => {
  set(lookupAtom, {
    ...get(lookupAtom),
    documentTypes: {
      data: get(lookupAtom).documentTypes.data,
      count: get(lookupAtom).documentTypes.count,
      isLoading: true,
      error: null,
    },
  });
  try {
    const documentTypes = await new GetDocumentTypeUseCase(lookupRepository).execute();
    set(lookupAtom, {
      ...get(lookupAtom),
      documentTypes: {
        data: documentTypes.documentTypes,
        count: documentTypes.count,
        isLoading: false,
        error: null,
      },
    });
  } catch (error: unknown) {
    set(lookupAtom, {
      ...get(lookupAtom),
      documentTypes: {
        data: get(lookupAtom).documentTypes.data,
        count: get(lookupAtom).documentTypes.count,
        isLoading: false,
        error: error as string,
      },
    });
  }
});

/**
 * Adjustment Types
 */
export const getAdjustmentTypesAtom = atom(null, async (get, set) => {
  set(lookupAtom, {
    ...get(lookupAtom),
    adjustmentTypes: {
      data: get(lookupAtom).adjustmentTypes.data,
      count: get(lookupAtom).adjustmentTypes.count,
      isLoading: true,
      error: null,
    },
  });
  try {
    const adjustmentTypes = await new GetAdjustmentTypeUseCase(lookupRepository).execute();
    set(lookupAtom, {
      ...get(lookupAtom),
      adjustmentTypes: {
        data: adjustmentTypes.adjustmentTypes,
        count: adjustmentTypes.count,
        isLoading: false,
        error: null,
      },
    });
  } catch (error: unknown) {
    set(lookupAtom, {
      ...get(lookupAtom),
      adjustmentTypes: {
        data: get(lookupAtom).adjustmentTypes.data,
        count: get(lookupAtom).adjustmentTypes.count,
        isLoading: false,
        error: error as string,
      },
    });
  }
});

/**
 * Adjustment Categories
 */
export const getAdjustmentCategoriesAtom = atom(null, async (get, set) => {
  set(lookupAtom, {
    ...get(lookupAtom),
    adjustmentCategories: {
      data: get(lookupAtom).adjustmentCategories.data,
      count: get(lookupAtom).adjustmentCategories.count,
      isLoading: true,
      error: null,
    },
  });
  try {
    const adjustmentCategories = await new GetAdjustmentCategoryUseCase(lookupRepository).execute();
    set(lookupAtom, {
      ...get(lookupAtom),
      adjustmentCategories: {
        data: adjustmentCategories.adjustmentCategories,
        count: adjustmentCategories.count,
        isLoading: false,
        error: null,
      },
    });
  } catch (error: unknown) {
    set(lookupAtom, {
      ...get(lookupAtom),
      adjustmentCategories: {
        data: get(lookupAtom).adjustmentCategories.data,
        count: get(lookupAtom).adjustmentCategories.count,
        isLoading: false,
        error: error as string,
      },
    });
  }
});

/**
 * Deduction Type Categories
 */
export const getDeductionTypeCategoriesAtom = atom(null, async (get, set) => {
  set(lookupAtom, {
    ...get(lookupAtom),
    deductionTypeCategories: {
      data: get(lookupAtom).deductionTypeCategories.data,
      count: get(lookupAtom).deductionTypeCategories.count,
      isLoading: true,
      error: null,
    },
  });
  try {
    const deductionTypeCategories = await new GetDeductionTypeCategoryUseCase(lookupRepository).execute();
    set(lookupAtom, {
      ...get(lookupAtom),
      deductionTypeCategories: {
        data: deductionTypeCategories.deductionTypeCategories,
        count: deductionTypeCategories.count,
        isLoading: false,
        error: null,
      },
    });
  } catch (error: unknown) {
    set(lookupAtom, {
      ...get(lookupAtom),
      deductionTypeCategories: {
        data: get(lookupAtom).deductionTypeCategories.data,
        count: get(lookupAtom).deductionTypeCategories.count,
        isLoading: false,
        error: error as string,
      },
    });
  }
});

/**
 * Deduction Document SubTypes
 */
export const getDocumentSubTypesAtom = atom(null, async (get, set) => {
  set(lookupAtom, {
    ...get(lookupAtom),
    documentSubTypes: {
      data: get(lookupAtom).documentSubTypes.data,
      count: get(lookupAtom).documentSubTypes.count,
      isLoading: true,
      error: null,
    },
  });
  try {
    const documentSubTypes = await new GetDocumentSubtypeUseCase(lookupRepository).execute();
    set(lookupAtom, {
      ...get(lookupAtom),
      documentSubTypes: {
        data: documentSubTypes.documentSubTypes,
        count: documentSubTypes.count,
        isLoading: false,
        error: null,
      },
    });
  } catch (error: unknown) {
    set(lookupAtom, {
      ...get(lookupAtom),
      documentSubTypes: {
        data: get(lookupAtom).documentSubTypes.data,
        count: get(lookupAtom).documentSubTypes.count,
        isLoading: false,
        error: error as string,
      },
    });
  }
});

/**
 * Accounting Systems
 */
export const getAccountingSystemsAtom = atom(null, async (get, set) => {
  set(lookupAtom, {
    ...get(lookupAtom),
    accountingSystems: {
      data: get(lookupAtom).accountingSystems.data,
      count: get(lookupAtom).accountingSystems.count,
      isLoading: true,
      error: null,
    },
  });
  try {
    const accountingSystems = await new GetAccountingSystemUseCase(lookupRepository).execute();
    set(lookupAtom, {
      ...get(lookupAtom),
      accountingSystems: {
        data: accountingSystems.accountingSystems,
        count: accountingSystems.count,
        isLoading: false,
        error: null,
      },
    });
  } catch (error: unknown) {
    set(lookupAtom, {
      ...get(lookupAtom),
      accountingSystems: {
        data: get(lookupAtom).accountingSystems.data,
        count: get(lookupAtom).accountingSystems.count,
        isLoading: false,
        error: error as string,
      },
    });
  }
});

/**
 * Promo Types
 */
export const getPromoTypesAtom = atom(null, async (get, set) => {
  set(lookupAtom, {
    ...get(lookupAtom),
    promoTypes: {
      data: get(lookupAtom).promoTypes.data,
      count: get(lookupAtom).promoTypes.count,
      isLoading: true,
      error: null,
    },
  });
  try {
    const promoTypes = await new GetPromoTypeUseCase(lookupRepository).execute();
    set(lookupAtom, {
      ...get(lookupAtom),
      promoTypes: {
        data: promoTypes.promoTypes,
        count: promoTypes.count,
        isLoading: false,
        error: null,
      },
    });
  } catch (error: unknown) {
    set(lookupAtom, {
      ...get(lookupAtom),
      promoTypes: {
        data: get(lookupAtom).promoTypes.data,
        count: get(lookupAtom).promoTypes.count,
        isLoading: false,
        error: error as string,
      },
    });
  }
});

/**
 * Promo Statuses
 */
export const getPromoStatusesAtom = atom(null, async (get, set) => {
  set(lookupAtom, {
    ...get(lookupAtom),
    promoStatuses: {
      data: get(lookupAtom).promoStatuses.data,
      count: get(lookupAtom).promoStatuses.count,
      isLoading: true,
      error: null,
    },
  });
  try {
    const promoStatuses = await new GetPromoStatusUseCase(lookupRepository).execute();
    set(lookupAtom, {
      ...get(lookupAtom),
      promoStatuses: {
        data: promoStatuses.promoStatuses,
        count: promoStatuses.count,
        isLoading: false,
        error: null,
      },
    });
  } catch (error: unknown) {
    set(lookupAtom, {
      ...get(lookupAtom),
      promoStatuses: {
        data: get(lookupAtom).promoStatuses.data,
        count: get(lookupAtom).promoStatuses.count,
        isLoading: false,
        error: error as string,
      },
    });
  }
});

/**
 * Dispute Tracking Statuses
 */
export const getDisputeTrackingStatusesAtom = atom(null, async (get, set) => {
  set(lookupAtom, {
    ...get(lookupAtom),
    disputeTrackingStatuses: {
      data: get(lookupAtom).disputeTrackingStatuses.data,
      count: get(lookupAtom).disputeTrackingStatuses.count,
      isLoading: true,
      error: null,
    },
  });
  try {
    const disputeTrackingStatuses = await new GetDisputeTrackingStatusUseCase(lookupRepository).execute();
    set(lookupAtom, {
      ...get(lookupAtom),
      disputeTrackingStatuses: {
        data: disputeTrackingStatuses.disputeTrackingStatuses,
        count: disputeTrackingStatuses.count,
        isLoading: false,
        error: null,
      },
    });
  } catch (error: unknown) {
    set(lookupAtom, {
      ...get(lookupAtom),
      disputeTrackingStatuses: {
        data: get(lookupAtom).disputeTrackingStatuses.data,
        count: get(lookupAtom).disputeTrackingStatuses.count,
        isLoading: false,
        error: error as string,
      },
    });
  }
});

/**
 * Products
 */
export const getProductsAtom = atom(
  {
    search: null as string | null,
    page_number: null as number | null,
    page_size: null as number | null,
  },
  async (get, set, { search, page_number, page_size }) => {
    set(lookupAtom, {
      ...get(lookupAtom),
      products: {
        data: get(lookupAtom).products.data,
        count: get(lookupAtom).products.count,
        isLoading: true,
        error: null,
      },
    });
    try {
      const products = await new GetProductLookUpUseCase(lookupRepository).execute({
        search,
        page_number,
        page_size,
      });
      set(lookupAtom, {
        ...get(lookupAtom),
        products: {
          data: products.products,
          count: products.count,
          isLoading: false,
          error: null,
        },
      });
    } catch (error: unknown) {
      set(lookupAtom, {
        ...get(lookupAtom),
        products: {
          data: get(lookupAtom).products.data,
          count: get(lookupAtom).products.count,
          isLoading: false,
          error: error as string,
        },
      });
    }
  }
);
